import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";
import { NewLayerCommand } from "./NewLayerCommand";

// Remove a layer from anywhere in the tree. On `apply`, also captures the
// original location (parent group id + sibling-id-above) so `invert` can
// restore it via NewLayerCommand at the same position.

interface Location {
  parentId: string | undefined;
  insertAbove: string | undefined;
  layer: Layer;
}

export class DeleteLayerCommand implements Command {
  readonly kind = "delete-layer";
  private captured: Location | null = null;

  constructor(private readonly layerId: string) {}

  apply(doc: Document): Document {
    this.captured = findLocation(doc.layers, this.layerId, undefined);
    if (!this.captured) return doc;
    return { ...doc, layers: removeById(doc.layers, this.layerId) };
  }

  invert(before: Document, _after: Document): Command {
    const loc = this.captured ?? findLocation(before.layers, this.layerId, undefined);
    if (!loc) {
      // Layer wasn't found; invert is a no-op. Return a self-cancelling command.
      return new DeleteLayerCommand(this.layerId);
    }
    return new NewLayerCommand(loc.layer, loc.parentId, loc.insertAbove);
  }
}

function findLocation(
  layers: readonly Layer[],
  id: string,
  parentId: string | undefined,
): Location | null {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.id === id) {
      const below = i > 0 ? layers[i - 1]!.id : undefined;
      return { parentId, insertAbove: below, layer: l };
    }
    if (l.kind === "group") {
      const inner = findLocation(l.children, id, l.id);
      if (inner) return inner;
    }
  }
  return null;
}

function removeById(layers: readonly Layer[], id: string): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.id === id) continue;
    if (l.kind === "group") {
      const group: GroupLayer = { ...l, children: removeById(l.children, id) };
      out.push(group);
    } else {
      out.push(l);
    }
  }
  return out;
}
